// frontend/src/utils/analytics.js
// FRONTEND_CODE_VERSION = "v1.0.0"
// Purpose: Umami Analytics event tracking utilities
// Privacy-first analytics - no cookies, GDPR compliant

/**
 * Check if Umami is loaded and available
 * @returns {boolean} True if Umami is available
 */
const isUmamiLoaded = () => {
  return (
    typeof window !== "undefined" &&
    window.umami &&
    typeof window.umami.track === "function"
  );
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} eventData - Additional event data (optional)
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (!isUmamiLoaded()) {
    // In development, log events to console for debugging
    if (import.meta.env.MODE === "development") {
      console.log("📊 Analytics Event (dev):", eventName, eventData);
    }
    return;
  }

  try {
    window.umami.track(eventName, eventData);
    
    // Log in development for debugging
    if (import.meta.env.MODE === "development") {
      console.log("📊 Analytics Event:", eventName, eventData);
    }
  } catch (error) {
    console.error("Analytics tracking error:", error);
  }
};

/**
 * Track page view (usually automatic, but can be used for SPA navigation)
 * @param {string} url - Page URL
 * @param {string} referrer - Referrer URL (optional)
 */
export const trackPageView = (url, referrer) => {
  if (!isUmamiLoaded()) return;

  try {
    window.umami.trackView(url, referrer);
  } catch (error) {
    console.error("Analytics page view error:", error);
  }
};

/**
 * Pre-defined event helpers for common actions
 * Provides type-safe event tracking with consistent naming
 */
export const analytics = {
  // Search events
  search: (searchTerm, resultsCount, hasResults) => {
    trackEvent("search", {
      term: searchTerm,
      results: resultsCount,
      has_results: hasResults,
    });
  },

  // Deal interactions
  dealClick: (dealId, dealName, category, discount) => {
    trackEvent("deal_clicked", {
      deal_id: dealId,
      deal_name: dealName,
      category: category,
      discount: discount,
    });
  },

  dealFavorite: (dealId, isFavorited) => {
    trackEvent("deal_favorited", {
      deal_id: dealId,
      action: isFavorited ? "added" : "removed",
    });
  },

  dealTrack: (dealId, isFirstTrack) => {
    trackEvent("deal_tracked", {
      deal_id: dealId,
      is_first_track: isFirstTrack,
    });
  },

  // Category interactions
  categoryClick: (categoryKey, categoryName) => {
    trackEvent("category_clicked", {
      category: categoryKey,
      category_name: categoryName,
    });
  },

  // User actions
  signUpStart: () => {
    trackEvent("sign_up_started");
  },

  signUpComplete: (method, eventData = {}) => {
    trackEvent("sign_up_completed", {
      method: method, // 'email', 'google', etc.
      ...eventData, // Include referral_source, telegram_channel if available
    });
  },

  login: (method) => {
    trackEvent("login", {
      method: method,
    });
  },

  // Engagement
  scrollDepth: (depth) => {
    trackEvent("scroll_depth", {
      depth: depth, // percentage
    });
  },

  timeOnPage: (seconds) => {
    trackEvent("time_on_page", {
      seconds: seconds,
    });
  },

  // Generic track method for custom events
  track: (eventName, eventData = {}) => {
    trackEvent(eventName, eventData);
  },
};

export default analytics;

